import { useState, type RefObject } from 'react'

import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { TRIP_LIST_DESKTOP_LAYOUT_MIN_WIDTH } from './tripListLayout'

function isDesktopListWidth(width: number): boolean {
  // Round so sub-pixel grid widths near the breakpoint don't flutter.
  return Math.round(width) >= TRIP_LIST_DESKTOP_LAYOUT_MIN_WIDTH
}

/**
 * Desktop list uses a fixed multi-column table. When the right panel is narrower
 * than that table, snap to the mobile card list instead of horizontal scrolling.
 *
 * Defaults to desktop layout so the desktop surface does not flash mobile cards
 * before the first measurement.
 */
function useTripListDesktopLayout(rootRef: RefObject<HTMLElement | null>) {
  const [isDesktopLayout, setIsDesktopLayout] = useState(true)

  useIsomorphicLayoutEffect(() => {
    const node = rootRef.current
    if (!node) return

    const updateLayout = (width: number) => {
      const nextIsDesktopLayout = isDesktopListWidth(width)
      setIsDesktopLayout(prevIsDesktopLayout => (
        prevIsDesktopLayout === nextIsDesktopLayout ? prevIsDesktopLayout : nextIsDesktopLayout
      ))
    }

    updateLayout(node.getBoundingClientRect().width)

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      const width = entry?.borderBoxSize?.[0]?.inlineSize ?? entry?.contentRect.width ?? node.getBoundingClientRect().width
      updateLayout(width)
    })
    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [rootRef])

  return isDesktopLayout
}

export default useTripListDesktopLayout
export { isDesktopListWidth }
